/**
 * https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_flatten
 * https://github.com/you-dont-need-x/you-dont-need-lodash
 */
import { fDateTime } from 'src/utils/format-time';

// ----------------------------------------------------------------------

export function flattenArray(list, key = 'children') {
  let children = [];

  const flatten = list?.map((item) => {
    if (item[key] && item[key].length) {
      children = [...children, ...item[key]];
    }
    return item;
  });

  return flatten?.concat(children.length ? flattenArray(children, key) : children);
}

// ----------------------------------------------------------------------

export function flattenDeep(array) {
  const isArray = array && Array.isArray(array);

  if (isArray) {
    return array.flat(Infinity);
  }
  return [];
}

// ----------------------------------------------------------------------

export function orderBy(array, properties, orders) {
  return array.slice().sort((a, b) => {
    for (let i = 0; i < properties.length; i += 1) {
      const property = properties[i];
      const order = orders && orders[i] === 'desc' ? -1 : 1;

      const aValue = a[property];
      const bValue = b[property];

      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
    }
    return 0;
  });
}

// ----------------------------------------------------------------------

export function keyBy(array, key) {
  return (array || []).reduce((result, item) => {
    const keyValue = key ? item[key] : item;

    return { ...result, [String(keyValue)]: item };
  }, {});
}

// ----------------------------------------------------------------------

export function sumBy(array, iteratee) {
  return array.reduce((sum, item) => sum + iteratee(item), 0);
}

// ----------------------------------------------------------------------

export function isEqual(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (typeof a === 'string' || typeof a === 'number' || typeof a === 'boolean') {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => isEqual(a[key], b[key]));
  }

  return false;
}

// ----------------------------------------------------------------------

function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export const merge = (target, ...sources) => {
  if (!sources.length) return target;

  const source = sources.shift();

  // eslint-disable-next-line no-restricted-syntax
  for (const key in source) {
    if (isObject(source[key])) {
      if (!target[key]) Object.assign(target, { [key]: {} });
      merge(target[key], source[key]);
    } else {
      Object.assign(target, { [key]: source[key] });
    }
  }

  return merge(target, ...sources);
};

export const downloadURI = (uri, name) => {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  link.click();
};

export const downloadBlob = (data, name = 'Package.zip') => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
};

const EXPONENTIAL_RE = /^([+-]?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/;

export const toPlainNumberString = (num) => {
  const asString = String(num);
  const match = EXPONENTIAL_RE.exec(asString);

  if (!match) return asString;

  const [, sign, intPart, fracPart = '', exponent] = match;
  const digits = intPart + fracPart;
  const pointIndex = intPart.length + Number(exponent);

  if (pointIndex <= 0) return `${sign}0.${'0'.repeat(-pointIndex)}${digits}`;
  if (pointIndex >= digits.length)
    return `${sign}${digits}${'0'.repeat(pointIndex - digits.length)}`;

  return `${sign}${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
};

const PLAIN_NUMBER_TOKEN = '__plain_number_7f3a__';
const PLAIN_NUMBER_TOKEN_RE = new RegExp(
  `"${PLAIN_NUMBER_TOKEN}(-?[\\d.]+)${PLAIN_NUMBER_TOKEN}"`,
  'g'
);

export const stringifyJsonWithPlainNumbers = (data, space = 2) =>
  JSON.stringify(
    data,
    (key, value) =>
      typeof value === 'number' && Number.isFinite(value) && /e/i.test(String(value))
        ? `${PLAIN_NUMBER_TOKEN}${toPlainNumberString(value)}${PLAIN_NUMBER_TOKEN}`
        : value,
    space
  )?.replace(PLAIN_NUMBER_TOKEN_RE, '$1');

export const getDataByKey = (data, key) => {
  switch (key) {
    case 'createdAt':
      return fDateTime(data[key]);
    default:
      return data[key];
  }
};

export const getCurrentEnv = () => process.env.REACT_APP_ENVIRONMENT;

// LoadTestResultStatus enum order, used to normalize a numeric status into its name
// (the API may serialize the enum as a number depending on server config).
const LOAD_TEST_STATUS_BY_INDEX = [
  'NotStarted',
  'Success',
  'PassedWithThresholdViolations',
  'Failed',
  'Running',
];

export const normalizeLoadTestStatus = (value) => {
  if (value === null || value === undefined) return null;
  // The endpoint may return either the bare status or a { status, id } object.
  const status = typeof value === 'object' ? value.status : value;
  if (status === null || status === undefined) return null;
  return typeof status === 'number' ? (LOAD_TEST_STATUS_BY_INDEX[status] ?? null) : status;
};

const isProductionEnvName = (env) => Boolean(env?.toLowerCase().includes('production'));

// Dev/QA aid: set `all-features` to `true` in local storage to turn on the env-gated features of
// every environment at once, so any build can exercise them without being that environment. Only
// feature gating is affected — the API base url still comes from the build env, so this never points
// the app at another backend. Ignored on production builds, otherwise it could be used to switch off
// a production-only guard.
export const hasAllEnvFeatures = () => {
  if (isProductionEnvName(getCurrentEnv())) {
    return false;
  }

  return localStorage.getItem('all-features') === 'true';
};

// Whether the features gated behind `env` are active: either this build is that env, or all env
// features are turned on.
export const hasEnvFeatures = (env) => hasAllEnvFeatures() || getCurrentEnv() === env;

export const isProductionEnv = () => hasAllEnvFeatures() || isProductionEnvName(getCurrentEnv());

// The incident assistant and the game rules behind it exist only where the orchestrator runs.
const INCIDENT_ENVS = ['Development', 'Testing', 'Production'];

export const hasIncidentFeatures = () =>
  hasAllEnvFeatures() || INCIDENT_ENVS.includes(getCurrentEnv() || 'Development');
